
// Phase 3: Language switcher dropdown + i18n gate.
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Check, Globe } from "lucide-react";
import i18n, { LANGUAGES, isRtl } from "@/lib/i18n";

export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { t, i18n: i18nInst } = useTranslation();
  const current = i18nInst.language || "en";
  const currentLang = LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? "icon" : "sm"}
          className={compact ? "h-9 w-9" : "h-9 gap-1.5 px-2"}
          aria-label={`${t("language.label")}: ${currentLang.nativeLabel}`}
        >
          <Globe className="h-4 w-4" />
          {!compact && (
            <span className="text-xs font-semibold uppercase">{current}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t("language.label")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => i18n.changeLanguage(l.code)}
            className="flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <span className="text-base leading-none">{l.flag}</span>
              <span className="font-medium">{l.nativeLabel}</span>
              {l.rtl && (
                <span className="rounded bg-muted px-1 text-[9px] font-bold text-muted-foreground">
                  RTL
                </span>
              )}
            </span>
            {current === l.code && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// I18nGate ensures <html lang/dir> are set on first client render and kept
// in sync. Mounted once inside <Providers>.
export function I18nGate() {
  const { i18n: i18nInst } = useTranslation();

  useEffect(() => {
    const apply = (lng: string) => {
      const dir = isRtl(lng) ? "rtl" : "ltr";
      document.documentElement.lang = lng;
      document.documentElement.dir = dir;
    };
    apply(i18nInst.language || "en");
    const handler = (lng: string) => apply(lng);
    i18n.on("languageChanged", handler);
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, [i18nInst]);

  return null;
}
